import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { QualityMeasure, Member } from '../types';
import {
  Award,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  XCircle,
  FileText,
  Calendar,
  Filter,
  Clock,
} from 'lucide-react';

interface QualityMeasureWithMember extends QualityMeasure {
  member?: Member;
}

export default function Quality() {
  const [measures, setMeasures] = useState<QualityMeasureWithMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    fetchQualityMeasures();
  }, []);

  const fetchQualityMeasures = async () => {
    try {
      const [measuresResult, membersResult] = await Promise.all([
        supabase
          .from('quality_measures')
          .select('*')
          .order('due_date', { ascending: true }),
        supabase.from('members').select('*'),
      ]);

      if (measuresResult.data && membersResult.data) {
        const measuresWithMembers = measuresResult.data.map((measure) => ({
          ...measure,
          member: membersResult.data.find((m) => m.id === measure.member_id),
        }));
        setMeasures(measuresWithMembers);
      }
    } catch (error) {
      console.error('Error fetching quality measures:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredMeasures = measures.filter((measure) => {
    if (statusFilter !== 'all' && measure.status !== statusFilter) return false;
    return true;
  });

  const stats = {
    compliantCount: measures.filter((m) => m.status === 'Compliant').length,
    pendingCount: measures.filter((m) => m.status === 'Pending').length,
    nonCompliantCount: measures.filter((m) => m.status === 'Non-Compliant')
      .length,
    complianceRate:
      measures.length > 0
        ? Math.round(
            (measures.filter((m) => m.status === 'Compliant').length /
              measures.length) *
              100
          )
        : 0,
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Compliant':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Non-Compliant':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getDaysUntilDue = (dueDate: string | null) => {
    if (!dueDate) return null;
    const due = new Date(dueDate);
    const now = new Date();
    const diff = due.getTime() - now.getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    return days;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading quality measures...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Quality & Compliance
        </h1>
        <p className="text-gray-600 mt-1">
          HEDIS and Stars quality measures tracking and compliance management
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border border-purple-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliance Rate</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {stats.complianceRate}%
              </p>
            </div>
            <div className="bg-purple-100 p-3 rounded-lg">
              <TrendingUp className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <div className="mt-4">
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-purple-600 h-2 rounded-full"
                style={{ width: `${stats.complianceRate}%` }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-green-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Compliant</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {stats.compliantCount}
              </p>
            </div>
            <div className="bg-green-100 p-3 rounded-lg">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
          </div>
          <p className="text-sm text-green-600 mt-4">All requirements met</p>
        </div>

        <div className="bg-white rounded-lg border border-amber-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Pending</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {stats.pendingCount}
              </p>
            </div>
            <div className="bg-amber-100 p-3 rounded-lg">
              <Clock className="w-8 h-8 text-amber-600" />
            </div>
          </div>
          <p className="text-sm text-amber-600 mt-4">Awaiting documentation</p>
        </div>

        <div className="bg-white rounded-lg border border-red-200 p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Non-Compliant</p>
              <p className="text-4xl font-bold text-gray-900 mt-2">
                {stats.nonCompliantCount}
              </p>
            </div>
            <div className="bg-red-100 p-3 rounded-lg">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <p className="text-sm text-red-600 mt-4">Requires attention</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex items-center space-x-4">
          <div className="flex items-center text-gray-700">
            <Filter className="w-5 h-5 mr-2" />
            <span className="font-medium">Filter by Status:</span>
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">All Statuses</option>
            <option value="Compliant">Compliant</option>
            <option value="Pending">Pending</option>
            <option value="Non-Compliant">Non-Compliant</option>
          </select>

          {statusFilter !== 'all' && (
            <button
              onClick={() => setStatusFilter('all')}
              className="px-3 py-2 text-sm text-purple-600 hover:text-purple-700 font-medium"
            >
              Clear filter
            </button>
          )}
        </div>
      </div>

      {/* HEDIS/Stars Measures Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                <Award className="w-5 h-5 text-purple-600 mr-2" />
                HEDIS / Stars Measures
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {filteredMeasures.length} measures
              </p>
            </div>
            <button className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors">
              Export Report
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Member
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Measure
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Year
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Evidence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Missing Documentation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Due Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredMeasures.map((measure) => {
                const daysUntilDue = getDaysUntilDue(measure.due_date);
                return (
                  <tr
                    key={measure.id}
                    className="hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {measure.member
                            ? `${measure.member.first_name} ${measure.member.last_name}`
                            : 'Unknown'}
                        </div>
                        <div className="text-sm text-gray-500">
                          {measure.member?.member_id}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <div className="text-sm font-medium text-gray-900">
                          {measure.measure_code}
                        </div>
                        <div className="text-sm text-gray-600">
                          {measure.measure_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {measure.measure_year}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                          measure.status
                        )}`}
                      >
                        {measure.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {measure.evidence_on_file ? (
                        <CheckCircle className="w-5 h-5 text-green-600" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600" />
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {measure.missing_documentation.length > 0 ? (
                        <ul className="text-sm text-gray-600 space-y-1">
                          {measure.missing_documentation.map((doc, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-red-500 mr-2">•</span>
                              {doc}
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <span className="text-sm text-green-600">
                          All documentation present
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                        <div>
                          <div className="text-sm text-gray-900">
                            {formatDate(measure.due_date)}
                          </div>
                          {daysUntilDue !== null && (
                            <div
                              className={`text-xs mt-1 ${
                                daysUntilDue < 0
                                  ? 'text-red-600 font-medium'
                                  : daysUntilDue < 30
                                  ? 'text-amber-600'
                                  : 'text-gray-500'
                              }`}
                            >
                              {daysUntilDue < 0
                                ? `${Math.abs(daysUntilDue)} days overdue`
                                : `${daysUntilDue} days remaining`}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Audit Log Section */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center mb-4">
          <FileText className="w-5 h-5 text-purple-600 mr-2" />
          <h2 className="text-lg font-semibold text-gray-900">
            Recent Audit Activity
          </h2>
        </div>

        <div className="space-y-3">
          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                Documentation uploaded for BCS measure
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Maria Rodriguez (MEM001) - Breast cancer screening evidence
                added
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Today at 2:45 PM by Care Manager
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                Missing documentation flagged
              </p>
              <p className="text-xs text-gray-600 mt-1">
                John Smith (MEM002) - HbA1c test results needed for CDC measure
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Today at 11:20 AM by System
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-shrink-0">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                Measure status updated to Compliant
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Sarah Johnson (MEM003) - COL measure completed with evidence
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Yesterday at 4:15 PM by Quality Team
              </p>
            </div>
          </div>

          <div className="flex items-start space-x-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <div className="flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-900">
                Due date approaching
              </p>
              <p className="text-xs text-gray-600 mt-1">
                Multiple members - AWV measures due within 30 days
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Yesterday at 9:00 AM by System
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
